import React, { useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import StarIcon from '@mui/icons-material/Star';
import LanguageIcon from '@mui/icons-material/Language';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import type { POI } from '../../types';

interface AddPOIDialogProps {
  open: boolean;
  selectedDay: number | string;
  availablePOIs: POI[];
  loading: boolean;
  adding: boolean;
  onClose: () => void;
  onAddPOI: (poi: POI) => void;
}

function AddPOIDialog({
  open,
  selectedDay,
  availablePOIs,
  loading,
  adding,
  onClose,
  onAddPOI,
}: AddPOIDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleCategoryChange = (category: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategory(isExpanded ? category : false);
  };

  const handleImageLoad = useCallback((imageUrl: string) => {
    setLoadedImages(prev => new Set(prev).add(imageUrl));
  }, []);

  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImages(prev => new Set(prev).add(imageUrl));
  }, []);

  const getIconForPOIType = (poi: POI) => {
    const poiTypeLower = (poi.poi_type || '').toLowerCase();
    const categoryLower = (poi.category || '').toLowerCase();

    if (poiTypeLower.includes('restaurant') || categoryLower.includes('restaurant')) {
      return <RestaurantIcon sx={{ fontSize: 48, color: 'grey.400' }} />;
    } else if (poiTypeLower.includes('bar') || categoryLower.includes('bar') ||
               poiTypeLower.includes('pub') || categoryLower.includes('pub')) {
      return <LocalBarIcon sx={{ fontSize: 48, color: 'grey.400' }} />;
    } else if (poiTypeLower.includes('cafe') || categoryLower.includes('cafe') ||
               poiTypeLower.includes('coffee') || categoryLower.includes('coffee')) {
      return <LocalCafeIcon sx={{ fontSize: 48, color: 'grey.400' }} />;
    }
    return <PlaceIcon sx={{ fontSize: 48, color: 'grey.400' }} />;
  };

  // Group POIs by poi_type
  const poisByType = availablePOIs.reduce((acc: Record<string, POI[]>, poi) => {
    const type = poi.poi_type || 'Other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(poi);
    return acc;
  }, {});

  const sortedTypes = Object.keys(poisByType).sort();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        Add Point of Interest to Day {selectedDay}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : availablePOIs.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No additional points of interest available for this city.
          </Typography>
        ) : (
          <Box>
            {sortedTypes.map((poiType) => (
              <Accordion
                key={poiType}
                expanded={expandedCategory === poiType}
                onChange={handleCategoryChange(poiType)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: 'primary.main',
                      textTransform: 'capitalize',
                    }}
                  >
                    {poiType}
                  </Typography>
                  <Chip
                    label={poisByType[poiType].length}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {poisByType[poiType].map((poi) => {
                      const hasOriginalImage = poi.images && poi.images.length > 0;
                      const imageLoaded = hasOriginalImage && loadedImages.has(poi.images![0]);
                      const imageFailed = hasOriginalImage && failedImages.has(poi.images![0]);

                      return (
                        <Card
                          key={poi.id}
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            flexDirection: 'row',
                            '&:hover': {
                              boxShadow: 4,
                              backgroundColor: 'action.hover',
                            },
                            opacity: adding ? 0.6 : 1,
                            pointerEvents: adding ? 'none' : 'auto',
                          }}
                          onClick={() => onAddPOI(poi)}
                        >
                          {hasOriginalImage && imageLoaded && !imageFailed ? (
                            <CardMedia
                              component="img"
                              sx={{
                                width: 200,
                                height: 150,
                                objectFit: 'cover',
                                flexShrink: 0,
                              }}
                              image={poi.images![0]}
                              alt={poi.name}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 200,
                                height: 150,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.200',
                                position: 'relative',
                              }}
                            >
                              {getIconForPOIType(poi)}
                              {hasOriginalImage && !imageLoaded && !imageFailed && (
                                <img
                                  src={poi.images![0]}
                                  alt=""
                                  style={{ display: 'none' }}
                                  onLoad={() => handleImageLoad(poi.images![0])}
                                  onError={() => handleImageError(poi.images![0])}
                                />
                              )}
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                            <CardContent sx={{ flex: 1, pb: 1 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {poi.name}
                              </Typography>

                              {poi.address && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                                  <PlaceIcon sx={{ fontSize: 16, color: 'action.active', mt: 0.25 }} />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {poi.address}
                                  </Typography>
                                </Box>
                              )}

                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                                {poi.category && (
                                  <Chip
                                    label={poi.category}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                )}

                                {poi.rating && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      {poi.rating}
                                    </Typography>
                                  </Box>
                                )}

                                {poi.website && (
                                  <IconButton
                                    component="a"
                                    href={poi.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    size="small"
                                    sx={{
                                      color: 'grey.500',
                                      '&:hover': {
                                        color: 'primary.main',
                                        backgroundColor: 'transparent'
                                      }
                                    }}
                                  >
                                    <LanguageIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                )}
                              </Box>

                              {poi.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {poi.description}
                                </Typography>
                              )}
                            </CardContent>
                            <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={adding}
                              >
                                Add
                              </Button>
                            </CardActions>
                          </Box>
                        </Card>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={adding}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddPOIDialog;
